






import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { ColourVariantType, ConfirmationModalSettings, StoreConstants, WorkflowJobSearchModel } from '@/types';
import DefaultWorkflowJob from '@/components/workflow/DefaultWorkflowJob.vue';
import AutomotiveWorkflowJob from '@/components/workflow/automotive/AutomotiveWorkflowJob.vue';
import DocuSignWorkflowJob from '@/components/workflow/docuSign/DocuSignWorkflowJob.vue';
import ScriveWorkflowJob from '@/components/workflow/scrive/ScriveWorkflowJob.vue';
import LoadingSpinner from '@/components/shared/LoadingSpinner.vue';
import ErrorNotification from '@/components/shared/ErrorNotification.vue';
import { confirmationHelper } from '@/mixins';
import { EventListenerUtils } from '@/utils/eventListenerUtils';
import { WorkflowJobService } from '@/services/WorkflowJobService';
import { Components } from '@/customer';

export default Vue.extend({
  name: 'WorkflowJob',
  mixins: [confirmationHelper],
  data() {
    return {
      renderWorkflowJobComponent: true
    };
  },
  methods: {
    ...mapActions('workflowJobStore', ['getWorkflowJob', 'unloadWorkflowJob', 'releaseWorkflowJobLock', StoreConstants.WorkflowJob.Actions.SetHasWorkflowJobBeenNavigatedAwayFrom]),
    ...mapActions('documentViewStore', ['unloadDocument']),
    async initialise(this: any, queueId: number, jobId: number) {
      const workflowJobHasLock = await WorkflowJobService.getWorkflowJobHasLock(queueId, jobId);
      this[StoreConstants.WorkflowJob.Actions.SetHasWorkflowJobBeenNavigatedAwayFrom](false);
      if (workflowJobHasLock.isLocked && !workflowJobHasLock.isLockedByCurrentSessionId) {
        const confirmationModalSettings: ConfirmationModalSettings = {
          cancelVariant: ColourVariantType.Secondary,
          cancelTitle: 'Go Back',
          okVariant: ColourVariantType.Primary,
          okTitle: 'Open',
          title: 'Do you want to view the job in read-only mode?',
          headerBgVariant: ColourVariantType.Primary,
          headerTextVariant: ColourVariantType.White
        };
        const confirmed = await this.showConfirmationModalAsync(this.$bvModal, workflowJobHasLock.lockMessage, confirmationModalSettings);
        if (!confirmed) {
          this.$router.push({
            name: 'WorkflowQueue',
            params: {
              id: queueId
            }
          });
        } else {
          this.getWorkflowJob({ queueId, jobId, isReadonly: true } as WorkflowJobSearchModel);
        }
      } else {
        this.getWorkflowJob({ queueId, jobId, isReadonly: false } as WorkflowJobSearchModel);
      }
    },
    unloadWorkflowJobAndDocument(this: any) {
      this[StoreConstants.WorkflowJob.Actions.SetHasWorkflowJobBeenNavigatedAwayFrom](true);
      if (!this[StoreConstants.WorkflowJob.Getters.IsTabProcessing] && !this[StoreConstants.WorkflowJob.Getters.IsWorkflowJobProcessed]) {
        this.releaseWorkflowJobLock();
      }
      this.unloadWorkflowJob();
      this.unloadDocument();
    },
    releaseWorkflowJobLockWithKeepAliveOnPageDiscard(this: any, ev: PageTransitionEvent) {
      EventListenerUtils.handlePageDiscard(ev, () => this.releaseWorkflowJobLock(true));
    },
    async refreshJobComponentAsync(this: any) {
      // AA 19/10/2023
      // Running repair for forcing the dynamic component to re-render.
      // Vue appears to re-use the component (and its data) despite the route path key on the vue-router (which is supposed to trigger a refresh)
      // This caused errors because the new job component still had data (e.g. tabs) from the previous job
      this.renderWorkflowJobComponent = false;
      await this.$nextTick(() => {
        this.renderWorkflowJobComponent = true;
      });
    }
  },
  computed: {
    ...mapGetters('workflowJobStore', [
      'workflowJob',
      'isLoading',
      'isWorkFlowJobDirty',
      StoreConstants.WorkflowJob.Getters.IsTabProcessing,
      StoreConstants.WorkflowJob.Getters.HasWorkflowJobBeenNavigatedAwayFrom,
      StoreConstants.WorkflowJob.Getters.IsWorkflowJobProcessed
    ]),
    jobComponentKey(this: any) {
      return this.$route.path;
    }
  },
  async created(this: any) {
    await this.refreshJobComponentAsync();
    EventListenerUtils.addPageHideEvent(this.releaseWorkflowJobLockWithKeepAliveOnPageDiscard);
  },

  beforeDestroy(this: any) {
    this.unloadWorkflowJobAndDocument();
    EventListenerUtils.removePageHideEvent(this.releaseWorkflowJobLockWithKeepAliveOnPageDiscard);
  },
  mounted() {
    this.initialise(parseInt(this.$route.params.queueId, 10), parseInt(this.$route.params.jobId, 10));
  },
  components: {
    DefaultWorkflowJob,
    LoadingSpinner,
    ErrorNotification,
    AutomotiveWorkflowJob,
    DocuSignWorkflowJob,
    ScriveWorkflowJob,
    ...Components.WorkflowJob
  }
});
